import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  ChevronDown,
} from 'lucide-react';
import './MusicPlayer.css';
import api from '../../services/api';
import { getTrackImageKey, getTrackImageType, resolveAssetKey, resolveCatalogTrackImage } from '../../utils/trackImage';

const safePlay = (audio, onPlayFailed) => {
  if (!audio?.src) return;
  audio.play().catch((error) => {
    if (error.name === 'AbortError') return;
    console.log('❌ Play failed:', error);
    onPlayFailed();
  });
};

const MusicPlayer = () => {
  const { 
    currentTrack, 
    isPlaying, 
    playPause, 
    playNext, 
    playPrevious,
    playlist,
    currentIndex,
    volume, 
    isMuted, 
    setVolumeLevel, 
    toggleMute,
    setIsPlaying
  } = useMusicPlayer();
  const [isPlayerVisible, setIsPlayerVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [trackImageUrl, setTrackImageUrl] = useState(null);
  
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  const displayTrackRef = useRef(currentTrack);
  const loadGenerationRef = useRef(0);
  const retryAttemptedRef = useRef(false);
  const wasPlayingRef = useRef(false);
  const reloadAudioRef = useRef(null);
  isPlayingRef.current = isPlaying;
  displayTrackRef.current = currentTrack;

  const handlePlayFailed = useCallback(() => setIsPlaying(false), [setIsPlaying]);

  const displayTrack = currentTrack;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < playlist.length - 1;
  const playNextRef = useRef(playNext);
  playNextRef.current = playNext;

  const getAudioUrl = useCallback(async (track) => {
    if (!track?.url) return null;

    if (track.url.startsWith('http')) {
      return track.url;
    }

    try {
      const params = { url: track.url };
      if (track.status) {
        params.status = track.status;
      }
      const { data } = await api.get('/api/media/presign', { params });
      return data.presignedUrl || null;
    } catch (error) {
      console.error('Failed to fetch presigned URL:', error);
      return null;
    }
  }, []);

  const reloadAudio = useCallback(async (track, shouldPlay = false) => {
    if (!track?.url || !audioRef.current) {
      return false;
    }

    const generation = ++loadGenerationRef.current;
    setIsLoading(true);

    const audioUrl = await getAudioUrl(track);
    if (generation !== loadGenerationRef.current || !audioRef.current) {
      return false;
    }

    if (!audioUrl) {
      setIsLoading(false);
      return false;
    }

    retryAttemptedRef.current = false;
    const audio = audioRef.current;
    audio.src = audioUrl;
    audio.load();

    if (shouldPlay && audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      setIsLoading(false);
      safePlay(audio, handlePlayFailed);
    }

    return true;
  }, [getAudioUrl, handlePlayFailed]);

  reloadAudioRef.current = reloadAudio;

  const getAssetUrl = async (key) => {
    if (!key) return null;
    if (key.startsWith('http')) return key;
    try {
      const { data } = await api.get('/api/media/presign', { params: { url: key } });
      return data.presignedUrl || null;
    } catch (error) {
      console.error('Failed to fetch signed asset URL:', error);
      return null;
    }
  };

  // Handle audio time updates
  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      
      const updateTime = () => {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100);
      };
      
      const updateDuration = () => {
        setDuration(audio.duration);
        // Also reset loading state when metadata is loaded
        // console.log('Metadata loaded - resetting loading state');
        setIsLoading(false);
      };

      const handleError = async (e) => {
        console.error('❌ Audio error:', e);
        console.error('❌ Audio error details:', {
          error: e.target.error,
          networkState: e.target.networkState,
          readyState: e.target.readyState,
          src: e.target.src,
          currentSrc: e.target.currentSrc
        });

        if (!retryAttemptedRef.current && displayTrackRef.current && reloadAudioRef.current) {
          retryAttemptedRef.current = true;
          const recovered = await reloadAudioRef.current(
            displayTrackRef.current,
            isPlayingRef.current
          );
          if (recovered) {
            return;
          }
        }

        setIsLoading(false);
        setIsPlaying(false);
      };

      const handleLoadStart = () => {
        // console.log('🎵 Audio loadstart event fired');
        // console.log('🔍 Audio details:', {
        //   src: audio.src,
        //   readyState: audio.readyState,
        //   networkState: audio.networkState
        // });
        setIsLoading(true);
      };

      const handleCanPlay = () => {
        setIsLoading(false);
        if (isPlayingRef.current) {
          safePlay(audio, handlePlayFailed);
        }
      };

      const handleCanPlayThrough = () => {
        setIsLoading(false);
      };

      const handleLoadedData = () => {
        // console.log('🎵 Audio loadeddata event fired');
        // console.log('🔍 Audio ready state:', audio.readyState);
        setIsLoading(false);
      };

      const handleEnded = () => {
        playNextRef.current();
      };
      
      audio.addEventListener('timeupdate', updateTime);
      audio.addEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('error', handleError);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('loadeddata', handleLoadedData);
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('timeupdate', updateTime);
        audio.removeEventListener('loadedmetadata', updateDuration);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('loadeddata', handleLoadedData);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [isPlaying, setIsPlaying, handlePlayFailed]);

  // Handle track changes — always fetch a fresh presigned URL
  useEffect(() => {
    if (!displayTrack) {
      setProgress(0);
      setCurrentTime(0);
      setDuration(0);
      setIsLoading(false);
      setIsPlaying(false);
      wasPlayingRef.current = false;
      return;
    }

    if (!audioRef.current) {
      return;
    }

    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    wasPlayingRef.current = isPlayingRef.current;

    reloadAudio(displayTrack, isPlayingRef.current);

    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 10000);

    return () => {
      loadGenerationRef.current += 1;
      clearTimeout(loadingTimeout);
    };
  }, [displayTrack, reloadAudio, setIsPlaying]);

  useEffect(() => {
    let cancelled = false;

    const isCatalogTrack = displayTrack?.url && !displayTrack.url.startsWith('audio_staging/');
    const imageKey = getTrackImageKey(displayTrack);
    if (!imageKey) {
      setTrackImageUrl(null);
      return undefined;
    }

    (async () => {
      const key = isCatalogTrack
        ? resolveCatalogTrackImage(displayTrack).assetKey
        : resolveAssetKey(imageKey, getTrackImageType(displayTrack), displayTrack.status);
      const signedImageUrl = await getAssetUrl(key);
      if (!cancelled) {
        setTrackImageUrl(signedImageUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [displayTrack]);

  // Handle play/pause — refetch presigned URL when resuming after pause
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !displayTrack) return;

    if (!isPlaying) {
      wasPlayingRef.current = false;
      audio.pause();
      return;
    }

    const isResume = !wasPlayingRef.current;
    wasPlayingRef.current = true;

    if (isResume) {
      reloadAudio(displayTrack, true);
      return;
    }

    if (
      !isLoading &&
      audio.src &&
      audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    ) {
      safePlay(audio, handlePlayFailed);
    }
  }, [isPlaying, isLoading, displayTrack, reloadAudio, handlePlayFailed]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);


  const handleProgressClick = (e) => {
    if (audioRef.current && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const clickPercent = clickX / width;
      const newTime = clickPercent * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(clickPercent * 100);
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const root = document.documentElement;
    if (displayTrack && isPlayerVisible) {
      root.classList.add('audio-player-visible');
    } else {
      root.classList.remove('audio-player-visible');
    }
    return () => root.classList.remove('audio-player-visible');
  }, [displayTrack, isPlayerVisible]);

  // Don't render anything if there's no track
  if (!displayTrack) {
    return null;
  }

  return createPortal(
    <>
      <audio ref={audioRef} preload="metadata" />

      <div
        className={`audio-player-container ${
          isPlayerVisible ? 'audio-player-container--visible' : ''
        }`}
      >
        <button
          type="button"
          className={`audio-player-toggle-button ${
            isPlayerVisible ? 'audio-player-toggle-button--open' : ''
          }`}
          onClick={() => setIsPlayerVisible((v) => !v)}
          aria-label={isPlayerVisible ? 'Hide player' : 'Show player'}
        >
          <ChevronDown className="h-4 w-4" />
        </button>

        <div className="audio-player-inner">
          <div className="audio-player-progress-wrap">
            <span className="audio-player-time">{formatTime(currentTime)}</span>
            <div
              ref={progressRef}
              className="audio-player-progress-bar"
              onClick={handleProgressClick}
              role="presentation"
            >
              <div
                className="audio-player-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="audio-player-time">{formatTime(duration)}</span>
          </div>

          <div className="audio-player-controls">
            <div className="audio-player-track-info">
              {trackImageUrl ? (
                <img
                  src={trackImageUrl}
                  alt=""
                  className="audio-player-track-image"
                />
              ) : (
                <div className="audio-player-track-image audio-player-track-image--placeholder">
                  ♪
                </div>
              )}
              <div className="audio-player-track-text">
                <div className="audio-player-track-title">
                  {displayTrack.title || 'Untitled Track'}
                </div>
                <div className="audio-player-track-artist">
                  {displayTrack.artist_name || 'Unknown Artist'}
                </div>
              </div>
            </div>

            <div className="audio-player-main-controls">
              <button
                type="button"
                onClick={playPrevious}
                disabled={!hasPrevious}
                className="audio-player-control-btn"
                aria-label="Previous track"
              >
                <SkipBack className="h-4 w-4" />
              </button>

              <button
                type="button"
                onClick={playPause}
                disabled={isLoading && !currentTrack}
                className="audio-player-control-btn audio-player-play-btn"
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                {isLoading && currentTrack ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black" />
                ) : isPlaying ? (
                  <Pause className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4 ml-0.5" />
                )}
              </button>

              <button
                type="button"
                onClick={playNext}
                disabled={!hasNext}
                className="audio-player-control-btn"
                aria-label="Next track"
              >
                <SkipForward className="h-4 w-4" />
              </button>
            </div>

            <div className="audio-player-volume">
              <button
                type="button"
                onClick={toggleMute}
                className="audio-player-volume-btn"
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={(e) => setVolumeLevel(parseFloat(e.target.value))}
                className="audio-player-volume-slider"
                aria-label="Volume"
              />
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MusicPlayer;